import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Form, Input, Table } from "antd"
import React from "react"

import InputNumber from "../../../../../components/Input/InputNumber.tsx"
import { DocumentCard } from "../../../../../components/Proposal&Contracts/DocumentCard.tsx"
import { validateNumber } from "../../../../../utils/validation/number.ts"
import type {
  ProposalFormData,
  ProposalFormWarranty
} from "../proposal-form-data.tsx"

export const defaultProposalFormWarrantyItems: ProposalFormWarranty["items"] = [
  {
    warranty_type: "Labor",
    duration: "2",
    covers: "Workmanship",
    excludes: "Owner-supplied materials"
  },
  {
    warranty_type: "Structural",
    duration: "5",
    covers: "Frame & foundation",
    excludes: "Earthquake, flood, fire"
  },
  {
    warranty_type: "Electrical / Plumbing",
    duration: "1",
    covers: "Rough installation",
    excludes: "Fixtures, client equipment"
  }
]

type FieldType = ProposalFormWarranty["items"]

export function ProposalWarrantySection() {
  const key: keyof ProposalFormData = "warranty"

  return (
    <DocumentCard title="Warranty & Maintenance" name={key}>
      <Form.Item<ProposalFormData>
        name={[key, "description"]}
        label="Warranty Description"
      >
        <Input.TextArea placeholder="Enter description" rows={2} />
      </Form.Item>

      <Form.List name={[key, "items"]}>
        {(fields, { add, remove }) => (
          <Table
            pagination={false}
            scroll={{ x: "max-content" }}
            dataSource={fields}
            rowKey="key"
            bordered
            columns={[
              {
                title: "Type",
                dataIndex: "name",
                key: "warranty_type",
                render: (_, field) => (
                  <Form.Item<ProposalFormWarranty["items"]>
                    name={[field.name, "warranty_type"]}
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, whitespace: true, message: "Required" }
                    ]}
                  >
                    <Input placeholder="Enter type" />
                  </Form.Item>
                )
              },
              {
                title: "Duration",
                width: 185,
                dataIndex: "name",
                key: "duration",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "duration"]}
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, message: "Required" },
                      { validator: validateNumber }
                    ]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="0"
                      className="w-full"
                      max={99999999} // 8 digits before decimal
                      suffix={
                        <Form.Item<ProposalFormData> noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const val = getFieldValue([
                              key,
                              "items",
                              field.name,
                              "duration"
                            ])

                            return val == 1 ? "year" : "years"
                          }}
                        </Form.Item>
                      }
                    />
                  </Form.Item>
                )
              },
              {
                title: "Covers",
                dataIndex: "name",
                // width: 250,
                key: "covers",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "covers"]}
                    style={{ margin: 0 }}
                    rules={[{ required: false, whitespace: true }]}
                  >
                    <Input placeholder="Enter covers" />
                  </Form.Item>
                )
              },
              {
                title: "Excludes",
                dataIndex: "name",
                key: "excludes",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "excludes"]}
                    style={{ margin: 0 }}
                    rules={[{ required: false, whitespace: true }]}
                  >
                    <Input placeholder="Enter excludes" />
                  </Form.Item>
                )
              },
              {
                title: "",
                width: 50,
                render: (_, field) => (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                )
              }
            ]}
            footer={() => (
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Warranty Item
              </Button>
            )}
          />
        )}
      </Form.List>
    </DocumentCard>
  )
}
